import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import employeesService from '../services/employeesService';
import { useNotification } from '../context/NotificationContext';

const CONTRACT_TYPES = ['permanente', 'temporal', 'proyecto'];
const PAY_FREQUENCIES = ['semanal', 'quincenal', 'mensual', 'proyecto'];

// Contract types the backend requires an end date for.
const TEMPORARY_CONTRACT_TYPES = ['temporal', 'proyecto'];

// Days the daily rate is multiplied by to reach the amount actually paid each
// cycle. A month is treated as a flat 30 days. 'proyecto' has no cycle: the
// figure captured IS the total, so it multiplies by one.
const PAY_FREQUENCY_DAYS = {
  semanal: 7,
  quincenal: 15,
  mensual: 30,
  proyecto: 1
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

const CURP_PATTERN = /^[A-Z0-9]{18}$/;
const NSS_PATTERN = /^\d{11,12}$/;
const RFC_PATTERN = /^([A-ZÑ&]{3,4})(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])([A-Z\d]{3})$/;

/**
 * Flattens the server's DRF error body into one readable line. The backend
 * knows exactly which field and why; discarding that for a generic "failed to
 * save" is what makes these forms frustrating to debug.
 */
const formatServerError = (err) => {
  const data = err.response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  return Object.entries(data)
    .map(([field, messages]) =>
      `${field}: ${Array.isArray(messages) ? messages.join(' ') : messages}`)
    .join(' — ');
};

const EmployeeForm = ({ initialData = null, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const isEditMode = !!initialData?.id;

  const emergency = initialData?.emergency_contact || {};

  const [formData, setFormData] = useState({
    role: initialData?.role || '',
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    birth_date: initialData?.birth_date || '',
    address: initialData?.address || '',
    national_id: initialData?.national_id || '',
    phone: initialData?.phone || '',
    internal_phone: initialData?.internal_phone || '',
    tax_id: initialData?.tax_id || '',
    social_security_number: initialData?.social_security_number || '',
    daily_salary: initialData?.daily_salary || '',
    pay_frequency: initialData?.pay_frequency || 'quincenal',
    bank_name: initialData?.bank_name || '',
    bank_account_number: initialData?.bank_account_number || '',
    contract_type: initialData?.contract_type || 'permanente',
    hire_date: initialData?.hire_date || '',
    contract_end_date: initialData?.contract_end_date || '',
    emergency_name: emergency.name || '',
    emergency_phone: emergency.phone || '',
    emergency_relationship: emergency.relationship || ''
  });

  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await employeesService.listRoles();
        setRoles(res.data || []);
      } catch (err) {
        console.error('Error loading roles:', err);
        notify.error(t('common.failedToLoad'));
      }
    };
    loadRoles();
  }, [notify, t]);

  const requiresEndDate = TEMPORARY_CONTRACT_TYPES.includes(formData.contract_type);

  // What the employee actually receives each cycle, shown under the two inputs
  // that determine it. Hidden until the salary is a usable number, so the note
  // never reads "$0.00" while the field is still empty.
  const dailySalaryNumber = parseFloat(formData.daily_salary);
  const payTotal = dailySalaryNumber > 0
    ? dailySalaryNumber * (PAY_FREQUENCY_DAYS[formData.pay_frequency] ?? 1)
    : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Picking a role pre-fills the daily salary from that role's reference rate.
  // It stays editable — the rate is a starting point, not a ceiling. A role
  // with no reference rate leaves whatever was already typed alone, so an
  // unconfigured role never wipes a salary.
  const handleRoleChange = (e) => {
    const { value } = e.target;
    const selected = roles.find((role) => String(role.id) === String(value));
    const baseSalary = selected?.base_daily_salary;

    setFormData((prev) => ({
      ...prev,
      role: value,
      daily_salary: baseSalary != null ? String(baseSalary) : prev.daily_salary
    }));
  };

  // Switching back to a permanent contract clears any end date already typed,
  // so a stale value cannot be submitted for a contract that has no end.
  const handleContractTypeChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      contract_type: value,
      contract_end_date: TEMPORARY_CONTRACT_TYPES.includes(value)
        ? prev.contract_end_date
        : ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = t('employees.firstNameRequired');
    if (!formData.last_name.trim()) newErrors.last_name = t('employees.lastNameRequired');
    if (!formData.role) newErrors.role = t('employees.roleRequired');
    if (!formData.birth_date) newErrors.birth_date = t('employees.birthDateRequired');
    if (!formData.address.trim()) newErrors.address = t('employees.addressRequired');
    if (!formData.phone.trim()) newErrors.phone = t('employees.phoneRequired');
    if (!formData.hire_date) newErrors.hire_date = t('employees.hireDateRequired');

    if (!CURP_PATTERN.test(formData.national_id.trim().toUpperCase())) {
      newErrors.national_id = t('employees.nationalIdInvalid');
    }
    if (!NSS_PATTERN.test(formData.social_security_number.trim())) {
      newErrors.social_security_number = t('employees.socialSecurityInvalid');
    }
    if (formData.tax_id.trim() && !RFC_PATTERN.test(formData.tax_id.trim().toUpperCase())) {
      newErrors.tax_id = t('employees.taxIdInvalid');
    }
    if (!(parseFloat(formData.daily_salary) > 0)) {
      newErrors.daily_salary = t('employees.dailySalaryInvalid');
    }

    // The backend accepts an absent emergency contact but rejects a partial
    // one, and this form always sends the three keys — so blank fields would
    // reach the API as a partial object and 400. Requiring name and phone here
    // matches what the server will actually accept.
    if (!formData.emergency_name.trim()) {
      newErrors.emergency_name = t('employees.emergencyNameRequired');
    }
    if (!formData.emergency_phone.trim()) {
      newErrors.emergency_phone = t('employees.emergencyPhoneRequired');
    }

    if (requiresEndDate && !formData.contract_end_date) {
      newErrors.contract_end_date = t('employees.contractEndDateRequired');
    }
    if (
      formData.contract_end_date &&
      formData.hire_date &&
      formData.contract_end_date < formData.hire_date
    ) {
      newErrors.contract_end_date = t('employees.contractEndDateBeforeHire');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    role: formData.role,
    first_name: formData.first_name.trim(),
    last_name: formData.last_name.trim(),
    birth_date: formData.birth_date,
    address: formData.address.trim(),
    national_id: formData.national_id.trim().toUpperCase(),
    phone: formData.phone.trim(),
    internal_phone: formData.internal_phone.trim() || null,
    // A blank RFC must go as null, not '': the column is unique, and several
    // employees without one have to coexist.
    tax_id: formData.tax_id.trim() ? formData.tax_id.trim().toUpperCase() : null,
    social_security_number: formData.social_security_number.trim(),
    daily_salary: formData.daily_salary,
    pay_frequency: formData.pay_frequency,
    bank_name: formData.bank_name.trim() || null,
    bank_account_number: formData.bank_account_number.trim() || null,
    contract_type: formData.contract_type,
    hire_date: formData.hire_date,
    contract_end_date: formData.contract_end_date || null,
    emergency_contact: {
      name: formData.emergency_name.trim(),
      phone: formData.emergency_phone.trim(),
      relationship: formData.emergency_relationship.trim()
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = buildPayload();
      let employee;
      if (isEditMode) {
        const res = await employeesService.updateEmployee(initialData.id, payload);
        employee = res.data;
        notify.success(t('employees.employeeUpdated'));
      } else {
        const res = await employeesService.createEmployee(payload);
        employee = res.data;
        notify.success(t('employees.employeeCreated'));
      }
      onSuccess(employee);
    } catch (err) {
      notify.error(
        formatServerError(err)
        || (isEditMode ? t('common.failedToUpdate') : t('common.failedToCreate'))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset className="form-fieldset">
        <legend>{t('employees.identitySection')}</legend>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required" htmlFor="first_name">
              {t('employees.firstName')}
            </label>
            <input
              id="first_name"
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="form-input"
            />
            {errors.first_name && <span className="form-error">{errors.first_name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="last_name">
              {t('employees.lastName')}
            </label>
            <input
              id="last_name"
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="form-input"
            />
            {errors.last_name && <span className="form-error">{errors.last_name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="birth_date">
              {t('employees.birthDate')}
            </label>
            <input
              id="birth_date"
              type="date"
              name="birth_date"
              value={formData.birth_date}
              onChange={handleChange}
              className="form-input"
            />
            {errors.birth_date && <span className="form-error">{errors.birth_date}</span>}
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required" htmlFor="national_id">
              {t('employees.nationalId')}
            </label>
            <input
              id="national_id"
              type="text"
              name="national_id"
              value={formData.national_id}
              onChange={handleChange}
              className="form-input"
            />
            {errors.national_id && <span className="form-error">{errors.national_id}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="tax_id">
              {t('employees.taxId')}
            </label>
            <input
              id="tax_id"
              type="text"
              name="tax_id"
              value={formData.tax_id}
              onChange={handleChange}
              className="form-input"
            />
            {errors.tax_id && <span className="form-error">{errors.tax_id}</span>}
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="social_security_number">
              {t('employees.socialSecurityNumber')}
            </label>
            <input
              id="social_security_number"
              type="text"
              name="social_security_number"
              value={formData.social_security_number}
              onChange={handleChange}
              className="form-input"
            />
            {errors.social_security_number && (
              <span className="form-error">{errors.social_security_number}</span>
            )}
          </div>
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('employees.contactSection')}</legend>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required" htmlFor="phone">
              {t('employees.phone')}
            </label>
            <input
              id="phone"
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-input"
            />
            {errors.phone && <span className="form-error">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="internal_phone">
              {t('employees.internalPhone')}
            </label>
            <input
              id="internal_phone"
              type="text"
              name="internal_phone"
              value={formData.internal_phone}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="address">
              {t('employees.address')}
            </label>
            <input
              id="address"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="form-input"
            />
            {errors.address && <span className="form-error">{errors.address}</span>}
          </div>
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('employees.employmentSection')}</legend>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required" htmlFor="role">
              {t('table.role')}
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleRoleChange}
              className="form-select"
            >
              <option value="">{t('filters.selectValue')}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
            {errors.role && <span className="form-error">{errors.role}</span>}
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="contract_type">
              {t('employees.contractType')}
            </label>
            <select
              id="contract_type"
              name="contract_type"
              value={formData.contract_type}
              onChange={handleContractTypeChange}
              className="form-select"
            >
              {CONTRACT_TYPES.map((option) => (
                <option key={option} value={option}>
                  {t(`employees.contractTypeOptions.${option}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="hire_date">
              {t('employees.hireDate')}
            </label>
            <input
              id="hire_date"
              type="date"
              name="hire_date"
              value={formData.hire_date}
              onChange={handleChange}
              className="form-input"
            />
            {errors.hire_date && <span className="form-error">{errors.hire_date}</span>}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="contract_end_date">
            {t('employees.contractEndDate')}
          </label>
          <input
            id="contract_end_date"
            type="date"
            name="contract_end_date"
            value={formData.contract_end_date}
            onChange={handleChange}
            disabled={!requiresEndDate}
            className="form-input"
          />
          {errors.contract_end_date && (
            <span className="form-error">{errors.contract_end_date}</span>
          )}
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('employees.compensationSection')}</legend>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label required" htmlFor="daily_salary">
              {t('employees.dailySalary')}
            </label>
            <input
              id="daily_salary"
              type="text"
              inputMode="decimal"
              name="daily_salary"
              value={formData.daily_salary}
              onChange={handleChange}
              className="form-input"
            />
            {errors.daily_salary && <span className="form-error">{errors.daily_salary}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="pay_frequency">
              {t('employees.payFrequency')}
            </label>
            <select
              id="pay_frequency"
              name="pay_frequency"
              value={formData.pay_frequency}
              onChange={handleChange}
              className="form-select"
            >
              {PAY_FREQUENCIES.map((option) => (
                <option key={option} value={option}>
                  {t(`employees.payFrequencyOptions.${option}`)}
                </option>
              ))}
            </select>
          </div>

        </div>

        {/* Full width, outside the grid: the note belongs to both fields above
            it, and as a grid cell it would push the banking fields out of
            their columns. */}
        {payTotal !== null && (
          <p className="text-bold mb-20" data-testid="pay-total-note">
            {t(`employees.payTotals.${formData.pay_frequency}`, {
              amount: formatCurrency(payTotal)
            })}
          </p>
        )}

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label" htmlFor="bank_name">
              {t('employees.bankName')}
            </label>
            <input
              id="bank_name"
              type="text"
              name="bank_name"
              value={formData.bank_name}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bank_account_number">
              {t('employees.bankAccountNumber')}
            </label>
            <input
              id="bank_account_number"
              type="text"
              name="bank_account_number"
              value={formData.bank_account_number}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('employees.emergencyContactSection')}</legend>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required" htmlFor="emergency_name">
              {t('employees.emergencyName')}
            </label>
            <input
              id="emergency_name"
              type="text"
              name="emergency_name"
              value={formData.emergency_name}
              onChange={handleChange}
              className="form-input"
            />
            {errors.emergency_name && (
              <span className="form-error">{errors.emergency_name}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="emergency_phone">
              {t('employees.emergencyPhone')}
            </label>
            <input
              id="emergency_phone"
              type="text"
              name="emergency_phone"
              value={formData.emergency_phone}
              onChange={handleChange}
              className="form-input"
            />
            {errors.emergency_phone && (
              <span className="form-error">{errors.emergency_phone}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="emergency_relationship">
              {t('employees.emergencyRelationship')}
            </label>
            <input
              id="emergency_relationship"
              type="text"
              name="emergency_relationship"
              value={formData.emergency_relationship}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        </div>
      </fieldset>

      <div className="form-actions">
        <button type="submit" disabled={saving} className="btn btn-primary">
          {saving ? t('common.saving') : t('common.save')}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          {t('employees.backToList')}
        </button>
      </div>
    </form>
  );
};

export default EmployeeForm;
